import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import {
  functionCategoriesApi,
  FunctionCategory,
  CategoryFunction,
} from '../api/functionCategoriesApi';
import { functionsApi, SystemFunction } from '../api/functionsApi';
import { FunctionIconSelect } from '../components/common/FunctionIconSelect';
import { getFunctionIconDisplay, resolveIconSelectValue } from '../utils/functionIconOptions';

interface CategoryFormData {
  name: string;
  code: string;
  description: string;
  icon: string;
  isActive: boolean;
}

const emptyCategoryForm = (): CategoryFormData => ({
  name: '',
  code: '',
  description: '',
  icon: 'folder',
  isActive: true,
});

const reorderList = <T,>(list: T[], fromIndex: number, toIndex: number): T[] => {
  const result = [...list];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
};

export const FunctionCategories: React.FC = () => {
  const { t } = useLang();
  const { isSuperAdmin } = useAuth();

  const [categories, setCategories] = useState<FunctionCategory[]>([]);
  const [usingTemplate, setUsingTemplate] = useState(false);
  const [allFunctions, setAllFunctions] = useState<SystemFunction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatingDefaults, setCreatingDefaults] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'skip' | 'replace'>('skip');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  const [categoryFormModal, setCategoryFormModal] = useState<'add' | 'edit' | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryFormData>(emptyCategoryForm());
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);
  const [togglingCategoryId, setTogglingCategoryId] = useState<string | null>(null);

  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [editingOrderValue, setEditingOrderValue] = useState('');

  const [dragCategoryId, setDragCategoryId] = useState<string | null>(null);

  const [functionsModalCategory, setFunctionsModalCategory] = useState<FunctionCategory | null>(null);
  const [modalFunctions, setModalFunctions] = useState<CategoryFunction[]>([]);
  const [modalOrderDirty, setModalOrderDirty] = useState(false);
  const [savingModalOrder, setSavingModalOrder] = useState(false);
  const [dragFunctionIndex, setDragFunctionIndex] = useState<number | null>(null);

  const [showAddFunctionsPicker, setShowAddFunctionsPicker] = useState(false);
  const [selectedFunctionIds, setSelectedFunctionIds] = useState<Set<string>>(new Set());
  const [addingFunctions, setAddingFunctions] = useState(false);
  const [functionSearch, setFunctionSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [categoriesResponse, functionsData] = await Promise.all([
        functionCategoriesApi.getAllCategories(),
        functionsApi.getAllFunctions(),
      ]);
      setCategories(categoriesResponse.categories);
      setUsingTemplate(categoriesResponse.usingTemplate);
      setAllFunctions(functionsData);
    } catch (err: any) {
      console.error('Error loading categories:', err);
      setError(err.response?.data?.message || t('errorLoadingFunctionCategories'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.menuOrder - b.menuOrder),
    [categories],
  );

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [categories]);

  const assignedCategoryByFunctionId = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((category) => {
      (category.functions || []).forEach((func) => {
        map.set(func.id, category.id);
      });
    });
    return map;
  }, [categories]);

  const handleCreateDefaultCategories = async () => {
    try {
      setCreatingDefaults(true);
      setError(null);
      const result = await functionCategoriesApi.createDefaultCategories();
      if (result.categories?.length > 0) {
        alert(t('defaultFunctionCategoriesCreated').replace('{count}', String(result.categories.length)));
      } else {
        alert(result.message || t('noFunctionCategories'));
      }
      await loadData();
    } catch (err: any) {
      console.error('Error creating default categories:', err);
      setError(err.response?.data?.message || t('errorCreatingDefaultFunctionCategories'));
    } finally {
      setCreatingDefaults(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      await functionCategoriesApi.exportCategories();
      alert(t('exportFunctionCategoriesSuccess'));
    } catch (err: any) {
      console.error('Error exporting categories:', err);
      setError(err.response?.data?.message || t('errorLoadingFunctionCategories'));
    } finally {
      setExporting(false);
    }
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportError(null);
    setImportSuccess(null);
    setImportMode('skip');
    setShowImportModal(true);
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportError(t('clickToSelectFile'));
      return;
    }

    try {
      setImportLoading(true);
      setImportError(null);
      setImportSuccess(null);
      const result = await functionCategoriesApi.importCategories(importFile, importMode);

      let message = result.message;
      if (result.skippedCount > 0 && result.skippedCodes.length > 0) {
        message += ` (${result.skippedCodes.join(', ')})`;
      }
      if (result.missingFunctionCodes?.length > 0) {
        message += `\n${t('missingFunctionCodes')}: ${result.missingFunctionCodes.join(', ')}`;
      }
      if (result.errors.length > 0) {
        message += `\n${result.errors.join('\n')}`;
      }

      if (result.success) {
        setImportSuccess(message);
        await loadData();
      } else {
        setImportError(message);
      }
    } catch (err: any) {
      console.error('Error importing categories:', err);
      setImportError(err.response?.data?.message || t('errorLoadingFunctionCategories'));
    } finally {
      setImportLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!categoryForm.name.trim() || !categoryForm.code.trim()) return;
    try {
      setSavingCategory(true);
      await functionCategoriesApi.createCategory({
        name: categoryForm.name.trim(),
        code: categoryForm.code.toUpperCase().replace(/\s+/g, '_'),
        description: categoryForm.description.trim() || `${t('functionCategory')}: ${categoryForm.name.trim()}`,
        icon: categoryForm.icon.trim() || 'folder',
        menuOrder: categories.length + 1,
        isActive: categoryForm.isActive,
      });
      closeCategoryFormModal();
      await loadData();
      alert(t('functionCategoryCreatedSuccess'));
    } catch (err: any) {
      console.error('Error adding category:', err);
      setError(err.response?.data?.message || t('errorCreatingFunctionCategory'));
    } finally {
      setSavingCategory(false);
    }
  };

  const openAddModal = () => {
    setCategoryForm(emptyCategoryForm());
    setEditingCategoryId(null);
    setCategoryFormModal('add');
  };

  const openEditModal = (category: FunctionCategory) => {
    setCategoryForm({
      name: category.name,
      code: category.code,
      description: category.description || '',
      icon: resolveIconSelectValue(category.icon || ''),
      isActive: category.isActive,
    });
    setEditingCategoryId(category.id);
    setCategoryFormModal('edit');
  };

  const closeCategoryFormModal = () => {
    setCategoryFormModal(null);
    setEditingCategoryId(null);
    setCategoryForm(emptyCategoryForm());
  };

  const handleSaveCategory = async () => {
    if (!editingCategoryId || !categoryForm.name.trim() || !categoryForm.code.trim()) return;
    try {
      setSavingCategory(true);
      await functionCategoriesApi.updateCategory(editingCategoryId, {
        name: categoryForm.name.trim(),
        code: categoryForm.code.toUpperCase().replace(/\s+/g, '_'),
        description: categoryForm.description.trim() || undefined,
        icon: categoryForm.icon.trim() || 'folder',
        isActive: categoryForm.isActive,
      });
      closeCategoryFormModal();
      await loadData();
      alert(t('functionCategoryUpdatedSuccess'));
    } catch (err: any) {
      console.error('Error updating category:', err);
      setError(err.response?.data?.message || t('errorUpdatingFunctionCategory'));
    } finally {
      setSavingCategory(false);
    }
  };

  const handleSubmitCategoryForm = () => {
    if (categoryFormModal === 'add') {
      handleAddCategory();
    } else if (categoryFormModal === 'edit') {
      handleSaveCategory();
    }
  };

  const handleToggleCategoryActive = async (category: FunctionCategory) => {
    try {
      setTogglingCategoryId(category.id);
      await functionCategoriesApi.updateCategory(category.id, {
        isActive: !category.isActive,
      });
      await loadData();
    } catch (err: any) {
      console.error('Error toggling category status:', err);
      setError(err.response?.data?.message || t('errorUpdatingFunctionCategory'));
    } finally {
      setTogglingCategoryId(null);
    }
  };

  const handleDeleteCategory = async (category: FunctionCategory) => {
    const functionCount = category.functions?.length ?? 0;
    if (functionCount > 0) {
      alert(t('cannotDeleteCategoryWithFunctions'));
      return;
    }
    if (!window.confirm(t('confirmDeleteFunctionCategory'))) return;
    try {
      setDeletingCategoryId(category.id);
      await functionCategoriesApi.deleteCategory(category.id);
      await loadData();
      alert(t('functionCategoryDeletedSuccess'));
    } catch (err: any) {
      console.error('Error deleting category:', err);
      const message = err.response?.data?.message || t('errorDeletingFunctionCategory');
      setError(message);
      alert(message);
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const persistCategoryOrder = async (ordered: FunctionCategory[]) => {
    try {
      setSavingOrder(true);
      const items = ordered.map((cat, index) => ({
        id: cat.id,
        menuOrder: index + 1,
      }));
      const updated = await functionCategoriesApi.reorderCategories(items);
      setCategories(updated);
    } catch (err: any) {
      console.error('Error saving category order:', err);
      setError(err.response?.data?.message || t('errorUpdatingFunctionCategory'));
      await loadData();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleCategoryDrop = async (targetId: string) => {
    if (!dragCategoryId || dragCategoryId === targetId) return;
    const fromIndex = sortedCategories.findIndex((c) => c.id === dragCategoryId);
    const toIndex = sortedCategories.findIndex((c) => c.id === targetId);
    if (fromIndex < 0 || toIndex < 0) return;
    const reordered = reorderList(sortedCategories, fromIndex, toIndex);
    setCategories(reordered);
    setDragCategoryId(null);
    await persistCategoryOrder(reordered);
  };

  const handleSaveCategoryOrder = async (categoryId: string) => {
    const value = parseInt(editingOrderValue, 10);
    if (Number.isNaN(value) || value < 1) {
      setEditingOrderId(null);
      return;
    }
    const currentIndex = sortedCategories.findIndex((c) => c.id === categoryId);
    if (currentIndex < 0) return;
    const targetIndex = Math.min(Math.max(value - 1, 0), sortedCategories.length - 1);
    const reordered = reorderList(sortedCategories, currentIndex, targetIndex);
    setEditingOrderId(null);
    setCategories(reordered);
    await persistCategoryOrder(reordered);
  };

  const openFunctionsModal = (category: FunctionCategory, openAddPicker = false) => {
    const functions = [...(category.functions || [])].sort((a, b) => a.menuOrder - b.menuOrder);
    setFunctionsModalCategory(category);
    setModalFunctions(functions);
    setModalOrderDirty(false);
    setShowAddFunctionsPicker(openAddPicker);
    setSelectedFunctionIds(new Set());
    setFunctionSearch('');
  };

  const closeFunctionsModal = () => {
    if (modalOrderDirty && !window.confirm(t('unsavedOrderChanges'))) {
      return;
    }
    setFunctionsModalCategory(null);
    setModalFunctions([]);
    setModalOrderDirty(false);
    setShowAddFunctionsPicker(false);
    setSelectedFunctionIds(new Set());
    setFunctionSearch('');
  };

  const handleFunctionDrop = (targetIndex: number) => {
    if (dragFunctionIndex === null || dragFunctionIndex === targetIndex) return;
    const reordered = reorderList(modalFunctions, dragFunctionIndex, targetIndex);
    setModalFunctions(reordered);
    setModalOrderDirty(true);
    setDragFunctionIndex(null);
  };

  const handleSaveModalOrder = async () => {
    if (!functionsModalCategory) return;
    try {
      setSavingModalOrder(true);
      const functionIds = modalFunctions.map((f) => f.id);
      const updated = await functionCategoriesApi.reorderCategoryFunctions(
        functionsModalCategory.id,
        functionIds,
      );
      setFunctionsModalCategory(updated);
      setModalFunctions([...(updated.functions || [])].sort((a, b) => a.menuOrder - b.menuOrder));
      setModalOrderDirty(false);
      await loadData();
      alert(t('orderSaved'));
    } catch (err: any) {
      console.error('Error saving function order:', err);
      setError(err.response?.data?.message || t('errorUpdatingFunctionCategory'));
    } finally {
      setSavingModalOrder(false);
    }
  };

  const availableFunctionsToAdd = useMemo(() => {
    if (!functionsModalCategory) return [];
    const inCategory = new Set(modalFunctions.map((f) => f.id));
    const query = functionSearch.trim().toLowerCase();
    return allFunctions
      .filter((f) => !inCategory.has(f.id))
      .filter((f) => {
        if (!query) return true;
        return (
          f.name.toLowerCase().includes(query) ||
          f.code.toLowerCase().includes(query) ||
          (f.route || '').toLowerCase().includes(query)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allFunctions, modalFunctions, functionsModalCategory, functionSearch]);

  const handleAddSelectedFunctions = async () => {
    if (!functionsModalCategory || selectedFunctionIds.size === 0) return;
    try {
      setAddingFunctions(true);
      const updated = await functionCategoriesApi.assignFunctionsToCategory(
        functionsModalCategory.id,
        Array.from(selectedFunctionIds),
      );
      setFunctionsModalCategory(updated);
      setModalFunctions([...(updated.functions || [])].sort((a, b) => a.menuOrder - b.menuOrder));
      setShowAddFunctionsPicker(false);
      setSelectedFunctionIds(new Set());
      setFunctionSearch('');
      await loadData();
    } catch (err: any) {
      console.error('Error adding functions:', err);
      setError(err.response?.data?.message || t('errorUpdatingFunctionCategory'));
    } finally {
      setAddingFunctions(false);
    }
  };

  const handleRemoveFunction = async (functionId: string) => {
    if (!functionsModalCategory) return;
    if (!window.confirm(t('removeFromCategory') + '?')) return;
    try {
      const updated = await functionCategoriesApi.removeFunctionFromCategory(
        functionsModalCategory.id,
        functionId,
      );
      setFunctionsModalCategory(updated);
      setModalFunctions([...(updated.functions || [])].sort((a, b) => a.menuOrder - b.menuOrder));
      setModalOrderDirty(false);
      await loadData();
    } catch (err: any) {
      console.error('Error removing function:', err);
      setError(err.response?.data?.message || t('errorUpdatingFunctionCategory'));
    }
  };

  const toggleFunctionSelection = (id: string) => {
    setSelectedFunctionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">{t('mainManagement')}</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">{t('functions')}</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">{t('functionCategories')}</span>
      </nav>

      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('functionCategories')}</h1>
          <p className="text-gray-600 mt-1">{t('manageFunctionCategories')}</p>
          <p className="text-gray-500 text-sm mt-1">{t('dragToReorder')}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-6 py-3 bg-blue-50 text-blue-700 font-semibold rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
          >
            {exporting ? t('loading') : t('exportFunctionCategories')}
          </button>
          <button
            onClick={openImportModal}
            className="px-6 py-3 bg-green-50 text-green-700 font-semibold rounded-lg hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200"
          >
            {t('importFunctionCategories')}
          </button>
          {isSuperAdmin && (
            <button
              onClick={handleCreateDefaultCategories}
              disabled={creatingDefaults}
              className={`px-6 py-3 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all duration-200 shadow-lg ${
                creatingDefaults
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600'
              }`}
            >
              {creatingDefaults ? t('creating') : t('createDefaultFunctionCategories')}
            </button>
          )}
          <button
            onClick={openAddModal}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 shadow-lg"
          >
            + {t('addFunctionCategory')}
          </button>
        </div>
      </div>

      {usingTemplate && !isSuperAdmin && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800 text-sm">{t('functionCategoriesUsingTemplate')}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {savingOrder && (
        <div className="mb-4 text-sm text-cyan-700">{t('creating')}...</div>
      )}

      {categories.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📁</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noFunctionCategories')}</h3>
          <p className="text-gray-500 mb-6">{t('noFunctionCategoriesHelp')}</p>
          {isSuperAdmin ? (
            <button
              onClick={handleCreateDefaultCategories}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-600 shadow-lg"
            >
              {t('createDefaultFunctionCategories')}
            </button>
          ) : (
            <p className="text-sm text-gray-500">{t('functionCategoriesUsingTemplate')}</p>
          )}
        </div>
      )}

      {categories.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-3 py-4" />
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('menuOrder')}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('functionCategoryName')}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('functionCategoryCode')}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('functionsPreview')}
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedCategories.map((category, index) => {
                  const functions = [...(category.functions || [])].sort(
                    (a, b) => a.menuOrder - b.menuOrder,
                  );
                  return (
                    <tr
                      key={category.id}
                      draggable
                      onDragStart={() => setDragCategoryId(category.id)}
                      onDragEnd={() => setDragCategoryId(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleCategoryDrop(category.id)}
                      className={`hover:bg-gray-50 ${dragCategoryId === category.id ? 'opacity-50' : ''} ${
                        !category.isActive ? 'bg-gray-50 opacity-75' : ''
                      }`}
                    >
                      <td className="px-3 py-4 text-gray-400 cursor-grab active:cursor-grabbing">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                        </svg>
                      </td>
                      <td className="px-4 py-4">
                        {editingOrderId === category.id ? (
                          <input
                            type="number"
                            min={1}
                            max={sortedCategories.length}
                            value={editingOrderValue}
                            onChange={(e) => setEditingOrderValue(e.target.value)}
                            onBlur={() => handleSaveCategoryOrder(category.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveCategoryOrder(category.id);
                              if (e.key === 'Escape') setEditingOrderId(null);
                            }}
                            className="w-16 px-2 py-1 border border-cyan-300 rounded text-sm focus:ring-2 focus:ring-cyan-500"
                            autoFocus
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingOrderId(category.id);
                              setEditingOrderValue(String(index + 1));
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 text-gray-700 rounded-full text-sm font-semibold hover:bg-cyan-100 hover:text-cyan-800"
                            title={t('menuOrder')}
                          >
                            {index + 1}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-xl">{getFunctionIconDisplay(category.icon)}</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{category.name}</div>
                            <div className="text-xs text-gray-500">
                              {functions.length} {t('functionsInCategory')}
                            </div>
                            {!category.isActive && (
                              <div className="text-xs text-amber-600 mt-0.5">{t('hideFromMenu')}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 font-mono">{category.code}</td>
                      <td className="px-4 py-4">
                        {functions.length === 0 ? (
                          <span className="text-sm text-gray-400 italic">{t('noFunctionsInCategory')}</span>
                        ) : (
                          <ol className="text-sm text-gray-700 space-y-1 max-h-24 overflow-y-auto list-decimal list-inside">
                            {functions.map((func) => (
                              <li key={func.id} className="truncate">
                                {func.icon && <span className="mr-1">{func.icon}</span>}
                                {func.name}
                              </li>
                            ))}
                          </ol>
                        )}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => handleToggleCategoryActive(category)}
                          disabled={togglingCategoryId === category.id}
                          title={category.isActive ? t('hideCategoryFromMenu') : t('showCategoryInMenu')}
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full transition-colors disabled:opacity-50 ${
                            category.isActive
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {togglingCategoryId === category.id
                            ? '...'
                            : category.isActive
                              ? t('active')
                              : t('inactive')}
                        </button>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(category)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg"
                            title={t('editFunctionCategory')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteCategory(category)}
                            disabled={deletingCategoryId === category.id}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg disabled:opacity-50"
                            title={t('delete')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => openFunctionsModal(category)}
                            className="px-3 py-2 bg-cyan-50 text-cyan-700 text-sm font-medium rounded-lg hover:bg-cyan-100"
                          >
                            {t('viewFunctionsInCategory')}
                          </button>
                          <button
                            type="button"
                            onClick={() => openFunctionsModal(category, true)}
                            className="px-3 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-lg hover:bg-green-100"
                          >
                            {t('add')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {categoryFormModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {categoryFormModal === 'add' ? t('addFunctionCategory') : t('editFunctionCategory')}
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionCategoryName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t('enterFunctionCategoryName')}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                autoFocus
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionCategoryCode')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={categoryForm.code}
                onChange={(e) => setCategoryForm((f) => ({ ...f, code: e.target.value }))}
                placeholder={t('enterFunctionCategoryCode')}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 font-mono"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionCategoryDescription')}
              </label>
              <textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('functionCategoryIcon')}
              </label>
              <FunctionIconSelect
                value={categoryForm.icon}
                onChange={(e) => setCategoryForm((f) => ({ ...f, icon: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={categoryForm.isActive}
                onChange={(e) => setCategoryForm((f) => ({ ...f, isActive: e.target.checked }))}
                className="mt-1 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">{t('showInMenu')}</span>
                <p className="text-xs text-gray-500 mt-0.5">{t('categoryHiddenFromMenuHint')}</p>
              </div>
            </label>
            <div className="flex space-x-3">
              <button
                onClick={closeCategoryFormModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSubmitCategoryForm}
                disabled={!categoryForm.name.trim() || !categoryForm.code.trim() || savingCategory}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg disabled:opacity-50"
              >
                {savingCategory
                  ? t('creating')
                  : categoryFormModal === 'add'
                    ? t('add')
                    : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {functionsModalCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('manageCategoryFunctions')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {functionsModalCategory.icon ? getFunctionIconDisplay(functionsModalCategory.icon) : '📁'}{' '}
                  {functionsModalCategory.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">{t('dragToReorder')}</p>
              </div>
              <button
                type="button"
                onClick={closeFunctionsModal}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddFunctionsPicker(true);
                  setSelectedFunctionIds(new Set());
                  setFunctionSearch('');
                }}
                className="px-4 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-lg hover:bg-green-100"
              >
                + {t('addExistingFunction')}
              </button>
              <div className="flex items-center gap-2">
                {modalOrderDirty && (
                  <span className="text-xs text-amber-600">{t('unsavedOrderChanges')}</span>
                )}
                <button
                  type="button"
                  onClick={handleSaveModalOrder}
                  disabled={!modalOrderDirty || savingModalOrder}
                  className="px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 disabled:opacity-50"
                >
                  {savingModalOrder ? t('creating') : t('saveOrder')}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {showAddFunctionsPicker ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900">{t('addExistingFunctions')}</h4>
                    <button
                      type="button"
                      onClick={() => setShowAddFunctionsPicker(false)}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      {t('back')}
                    </button>
                  </div>
                  <input
                    type="text"
                    value={functionSearch}
                    onChange={(e) => setFunctionSearch(e.target.value)}
                    placeholder={t('selectFunctionsToAdd')}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  {availableFunctionsToAdd.length === 0 ? (
                    <p className="text-sm text-gray-500 italic py-4">{t('allFunctionsAssigned')}</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-80 overflow-y-auto">
                      {availableFunctionsToAdd.map((func) => (
                        <label
                          key={func.id}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFunctionIds.has(func.id)}
                            onChange={() => toggleFunctionSelection(func.id)}
                            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {func.icon && <span className="mr-1">{func.icon}</span>}
                              {func.name}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">{func.code}</div>
                            {assignedCategoryByFunctionId.get(func.id) && (
                              <div className="text-xs text-amber-600">
                                {t('currentCategory')}: {categoryNameById.get(assignedCategoryByFunctionId.get(func.id)!)}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddFunctionsPicker(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleAddSelectedFunctions}
                      disabled={selectedFunctionIds.size === 0 || addingFunctions}
                      className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {addingFunctions ? t('creating') : `${t('add')} (${selectedFunctionIds.size})`}
                    </button>
                  </div>
                </div>
              ) : modalFunctions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 mb-4">{t('noFunctionsInCategory')}</p>
                  <button
                    type="button"
                    onClick={() => setShowAddFunctionsPicker(true)}
                    className="px-4 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-lg hover:bg-green-100"
                  >
                    + {t('addExistingFunction')}
                  </button>
                </div>
              ) : (
                <ul className="space-y-2">
                  {modalFunctions.map((func, index) => (
                    <li
                      key={func.id}
                      draggable
                      onDragStart={() => setDragFunctionIndex(index)}
                      onDragEnd={() => setDragFunctionIndex(null)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => handleFunctionDrop(index)}
                      className={`flex items-center gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg ${
                        dragFunctionIndex === index ? 'opacity-50' : ''
                      }`}
                    >
                      <span className="text-gray-400 cursor-grab active:cursor-grabbing">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                        </svg>
                      </span>
                      <span className="w-6 h-6 flex items-center justify-center bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-600">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          {func.icon && <span className="mr-1">{func.icon}</span>}
                          {func.name}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">{func.code}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFunction(func.id)}
                        className="text-red-500 hover:text-red-700 text-sm px-2 py-1"
                        title={t('removeFromCategory')}
                      >
                        {t('removeFromCategory')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">{t('importFunctionCategories')}</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">{t('importFunctionCategoriesHint')}</p>

              <div className="mb-4 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="functionCategoryImportMode"
                    checked={importMode === 'skip'}
                    onChange={() => setImportMode('skip')}
                  />
                  <span className="text-sm text-gray-700">{t('importFunctionCategoriesModeSkip')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="functionCategoryImportMode"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                  />
                  <span className="text-sm text-gray-700">{t('importFunctionCategoriesModeReplace')}</span>
                </label>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
                <input
                  type="file"
                  accept=".json,application/json"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="function-category-import-file"
                />
                <label
                  htmlFor="function-category-import-file"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <span className="text-sm text-gray-700">
                    {importFile ? importFile.name : t('clickToSelectFile')}
                  </span>
                  <span className="text-xs text-gray-500">{t('functionCategoryImportJsonOnly')}</span>
                </label>
              </div>

              {importError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm whitespace-pre-line">
                  {importError}
                </div>
              )}

              {importSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-sm whitespace-pre-line">
                  {importSuccess}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {t('close')}
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || importLoading}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {importLoading ? t('importLoading') : t('importFunctionCategories')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
